import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
	CloseButton,
	Flex,
	Input,
	InputGroup,
	InputLeftElement,
	InputRightElement,
	useOutsideClick,
} from '@tempo/core';
import {SearchOutlinedIcon} from '@tempo/icons';
import SearchResultList from './SearchResultList';
import type {RenderResultFunction, SearchResultKeyExtractor} from './types';
import {useKeyboardEvent} from '@deezer/next/shared/hooks/useKeyboardEvent';

type InputProps = React.ComponentProps<typeof Input>;
type InputLeftElementProps = React.ComponentProps<typeof InputLeftElement>;
export type SearchInputProps<T> = {
	searchResults?: T[];
	isLoading?: boolean;
	error?: any;
	renderSearchResult: RenderResultFunction<T>;
	renderSearchSkeleton: () => React.ReactElement;
	renderSearchPlaceholder: () => React.ReactElement;
	renderSearchError?: () => React.ReactElement;
	searchResultKeyExtractor?: SearchResultKeyExtractor<T>;
	onSelectResult: (item: T, index: number) => void;
	inputLeftElement?: React.ReactElement;
	inputLeftElementProps?: InputLeftElementProps;
	onSearchClear?: () => void;
	hideInputLeftElement?: boolean;
	disableSearchClear?: boolean;
	isDisabled?: boolean;
} & InputProps;

const SearchInput = <T,>({
	id,
	value,
	error,
	size,
	searchResults,
	isLoading,
	renderSearchResult,
	renderSearchSkeleton,
	renderSearchPlaceholder,
	renderSearchError,
	searchResultKeyExtractor,
	onSelectResult,
	inputLeftElement,
	inputLeftElementProps,
	onSearchClear,
	hideInputLeftElement,
	disableSearchClear,
	isDisabled,
	...rest
}: SearchInputProps<T>) => {
	const [isFocused, setIsFocused] = useState<boolean>(false);
	const [index, setIndex] = useState(-1);
	const ref = useRef<HTMLDivElement>(null!);
	useOutsideClick({ref: ref, handler: () => setIsFocused(false)});

	const _searchResultKeyExtractor =
		searchResultKeyExtractor || ((item: T, i: number) => String(i));

	const handleResultClick = useCallback(
		(item: any, i: any) => {
			setIsFocused(false);
			onSelectResult(item, i);
		},
		[onSelectResult],
	);

	const handleSearchClear = useCallback(() => {
		if (onSearchClear) {
			onSearchClear();
		}
	}, [onSearchClear]);

	useEffect(() => {
		(() => {
			setIndex(-1);
		})();
	}, [searchResults]);

	useKeyboardEvent(
		e => {
			if (isFocused && searchResults) {
				if (e.code === 'ArrowUp' && index >= 1) {
					e.preventDefault();
					setIndex(index - 1);
					document
						.getElementById(
							`search-result-${_searchResultKeyExtractor(
								searchResults[index - 1],
								index - 1,
							)}`,
						)
						?.focus();
				} else if (e.code === 'ArrowDown' && index < searchResults.length - 1) {
					e.preventDefault();
					setIndex(index + 1);
					document
						.getElementById(
							`search-result-${_searchResultKeyExtractor(
								searchResults[index + 1],
								index + 1,
							)}`,
						)
						?.focus();
				} else if (e.code === 'ArrowUp' && index === 0) {
					e.preventDefault();
					document.getElementById(id || 'search-input')?.focus();
					setIndex(-1);
				}
			}
		},
		[isFocused, searchResults, index],
	);

	return (
		<Flex
			ref={ref}
			position="relative"
			alignItems="center"
			justifyContent="center"
			w="100%"
			transition="width 200ms ease-in"
			me="4"
		>
			<InputGroup size={size}>
				{!hideInputLeftElement && (
					<InputLeftElement pointerEvents="none" {...inputLeftElementProps}>
						{inputLeftElement || <SearchOutlinedIcon />}
					</InputLeftElement>
				)}
				<Input
					onFocus={() => {
						setIsFocused(true);
						setIndex(-1);
					}}
					type="text"
					value={value}
					w="100%"
					id={id || 'search-input'}
					isDisabled={isDisabled}
					{...rest}
				/>
				{!disableSearchClear && !isDisabled && (
					<InputRightElement>
						{value && (
							<CloseButton
								data-testid={`${id || 'search-input'}-close-button`}
								onClick={handleSearchClear}
							/>
						)}
					</InputRightElement>
				)}
			</InputGroup>
			{isFocused && (
				<SearchResultList
					id={id}
					results={searchResults}
					error={error}
					isLoading={isLoading}
					renderSearchResult={renderSearchResult}
					renderSkeleton={renderSearchSkeleton}
					renderPlaceholder={renderSearchPlaceholder}
					renderSearchError={renderSearchError}
					keyExtractor={_searchResultKeyExtractor}
					onSelectResult={handleResultClick}
				/>
			)}
		</Flex>
	);
};

export default SearchInput;
